import React from "react";
import { AGENT_TYPE } from "../../constants/agentTypes";

const SUPPORT_EMAIL = "support@sagelyn.ai";

interface Config {
  icon: string;
  heading: string;
  body: string;
  buttonLabel: string;
  subject: string;
}

function getConfig(agentType: string): Config | null {
  if (agentType === AGENT_TYPE.COMMUNICATION_SMS) {
    return {
      icon: "phone_iphone",
      heading: "Connect a phone number",
      body: "To enable SMS with your agent, reach out and we'll set up a dedicated number for you.",
      buttonLabel: "Request phone setup",
      subject: "Phone Number Setup Request",
    };
  }
  if (agentType === AGENT_TYPE.COMMUNICATION_EMAIL) {
    return {
      icon: "inbox",
      heading: "Connect your email / CRM",
      body: "To connect your email inbox or CRM to this agent, reach out and we'll get you configured.",
      buttonLabel: "Request email / CRM setup",
      subject: "Email & CRM Setup Request",
    };
  }
  if (agentType === AGENT_TYPE.VISIBILITY_MANAGER) {
    return {
      icon: "search",
      heading: "Connect Google Search Console",
      body: "Connect your Google Search Console so your Visibility Manager can read your real search performance and spot opportunities. Self-serve connection is coming soon — reach out and we'll link your property now.",
      buttonLabel: "Request Search Console setup",
      subject: "Google Search Console Connection",
    };
  }
  return null;
}

interface AgentConnectionPromptProps {
  agentType: string | null | undefined;
  /** Visual variant — 'card' (default) for onboarding reveal, 'inline' for form success banner */
  variant?: "card" | "inline";
}

export const AgentConnectionPrompt: React.FC<AgentConnectionPromptProps> = ({
  agentType,
  variant = "card",
}) => {
  if (!agentType) return null;
  const config = getConfig(agentType);
  if (!config) return null;

  const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(config.subject)}`;

  if (variant === "inline") {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-theme-border bg-theme-surface px-4 py-3 text-sm">
        <span className="material-icons mt-0.5 text-[18px] text-brand-primary">{config.icon}</span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="font-semibold text-theme-fg">{config.heading}</p>
          <p className="text-theme-fg-muted">{config.body}</p>
          <p className="text-theme-fg-muted">
            Email us at{" "}
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-brand-primary underline underline-offset-2 hover:text-brand-primary-hover"
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            and we'll reach out to get you set up.
          </p>
        </div>
      </div>
    );
  }

  // card variant (default — used in onboarding reveal)
  return (
    <div className="w-full rounded-2xl border border-theme-border bg-theme-surface p-5 text-left shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-ui-hover">
          <span className="material-icons text-[18px] text-brand-primary">{config.icon}</span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-[13px] font-semibold text-theme-fg">{config.heading}</p>
          <p className="text-[12px] text-theme-fg-muted">{config.body}</p>
          <p className="text-[12px] text-theme-fg-muted">
            Email us at{" "}
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-brand-primary underline underline-offset-2 hover:text-brand-primary-hover"
            >
              {SUPPORT_EMAIL}
            </a>{" "}
            and we'll reach out to get you set up.
          </p>
        </div>
      </div>
    </div>
  );
};
